"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { FlowParams } from "@/lib/flow-filters";

/*
 * Full UW-parity filter sidebar — draft/apply model.
 *
 * Every control edits the DRAFT params instantly (controlled inputs, no
 * blur-commit). Nothing refetches until Apply. The Apply button lights up
 * whenever the draft differs from the applied params.
 *
 * Labels are plain English; every non-obvious control has a "?" hover
 * explaining exactly what it does. Monochrome black/white, emerald reserved
 * for active states, red only for bearish semantics.
 */

interface Palette {
  bg: string;
  panel: string;
  line: string;
  lineSoft: string;
  text: string;
  textDim: string;
  accent: string;
  accentDim: string;
  green: string;
  greenDim: string;
  red: string;
  redDim: string;
  gold: string;
}

interface Props {
  open: boolean;
  /** Phone layout: render as a full-screen drawer instead of a docked panel. */
  mobile?: boolean;
  draft: FlowParams;
  dirty: boolean;
  activeCount: number;
  C: Palette;
  focusKey?: string | null;
  onFocusSettled?: () => void;
  onClose: () => void;
  onDraft: (key: string, value: string | null) => void;
  onApply: () => void;
}

const SECTORS = [
  "Technology",
  "Financial Services",
  "Healthcare",
  "Consumer Cyclical",
  "Communication Services",
  "Energy",
  "Industrials",
  "Consumer Defensive",
  "Basic Materials",
  "Real Estate",
  "Utilities",
];

const ISSUE_TYPES = [
  { v: "Common Stock", label: "Stocks", help: "Regular company shares like NVDA or TSLA." },
  { v: "ETF", label: "ETFs", help: "Exchange-traded funds like SPY, QQQ, IWM." },
  { v: "Index", label: "Indices", help: "Index products like SPX, SPXW, XSP, VIX. Uncheck to silence the SPX firehose." },
  { v: "ADR", label: "ADRs", help: "Foreign companies traded on US exchanges (BABA, TSM…)." },
];

// Options venue (OPRA) codes with readable names.
const EXCHANGES: { v: string; label: string }[] = [
  { v: "XCBO", label: "Cboe" },
  { v: "C2OX", label: "Cboe C2" },
  { v: "BATO", label: "Cboe BZX" },
  { v: "EDGO", label: "Cboe EDGX" },
  { v: "XPHL", label: "Nasdaq PHLX" },
  { v: "XISX", label: "Nasdaq ISE" },
  { v: "GMNI", label: "Nasdaq GEMX" },
  { v: "XBXO", label: "Nasdaq BX" },
  { v: "XNMS", label: "Nasdaq NOM" },
  { v: "MPRL", label: "MIAX Pearl" },
  { v: "EMLD", label: "MIAX Emerald" },
  { v: "XMIO", label: "MIAX" },
  { v: "ARCO", label: "NYSE Arca" },
  { v: "AMXO", label: "NYSE American" },
  { v: "XBOX", label: "BOX" },
  { v: "MXOP", label: "MEMX" },
];

// Common FMP/UW industry names (the flow feed's industry_type values).
const INDUSTRIES = [
  "Advertising Agencies", "Aerospace & Defense", "Airlines", "Aluminum",
  "Apparel Retail", "Asset Management", "Auto Manufacturers", "Auto Parts",
  "Banks - Diversified", "Banks - Regional", "Beverages - Non-Alcoholic",
  "Biotechnology", "Broadcasting", "Building Materials", "Capital Markets",
  "Chemicals", "Communication Equipment", "Computer Hardware",
  "Consumer Electronics", "Copper", "Credit Services", "Department Stores",
  "Diagnostics & Research", "Discount Stores", "Drug Manufacturers - General",
  "Education & Training Services", "Electronic Components",
  "Engineering & Construction", "Entertainment", "Farm Products",
  "Financial Data & Stock Exchanges", "Food Distribution", "Footwear & Accessories",
  "Gambling", "Gold", "Grocery Stores", "Healthcare Plans", "Home Improvement Retail",
  "Homebuilding & Construction", "Hotels & Motels", "Household Products",
  "Industrial Distribution", "Information Technology Services", "Insurance - Diversified",
  "Insurance - Life", "Insurance - Property & Casualty", "Internet Content & Information",
  "Internet Retail", "Leisure", "Lodging", "Machinery", "Medical Devices",
  "Medical Instruments & Supplies", "Mortgage Finance", "Oil & Gas E&P",
  "Oil & Gas Equipment & Services", "Oil & Gas Integrated", "Oil & Gas Midstream",
  "Oil & Gas Refining & Marketing", "Packaged Foods", "Personal Services",
  "Pharmaceutical Retailers", "Railroads", "REIT - Diversified", "REIT - Retail",
  "REIT - Specialty", "Residential Construction", "Restaurants", "Semiconductors",
  "Semiconductor Equipment & Materials", "Software - Application",
  "Software - Infrastructure", "Solar", "Specialty Chemicals", "Specialty Retail",
  "Steel", "Telecom Services", "Tobacco", "Travel Services", "Trucking", "Uranium",
  "Utilities - Regulated Electric", "Waste Management",
];

// OPRA trade-condition codes with plain-English meaning.
const TRADE_CODES: { v: string; label: string; help: string }[] = [
  { v: "AUTO", label: "AUTO", help: "Automatic electronic execution — the standard fill." },
  { v: "SLAN", label: "SLAN", help: "Single-leg auction, non-ISO. Price-improvement auction fill." },
  { v: "SLAI", label: "SLAI", help: "Single-leg auction ISO (intermarket sweep order)." },
  { v: "SLCN", label: "SLCN", help: "Single-leg cross, non-ISO. Matched buyer and seller." },
  { v: "SLFT", label: "SLFT", help: "Single-leg floor trade — executed on a physical floor." },
  { v: "MLET", label: "MLET", help: "Multi-leg electronic trade — part of a spread." },
  { v: "MLAT", label: "MLAT", help: "Multi-leg auction trade." },
  { v: "MLCT", label: "MLCT", help: "Multi-leg cross trade." },
  { v: "MLFT", label: "MLFT", help: "Multi-leg floor trade." },
  { v: "MESL", label: "MESL", help: "Multi-leg trade with a stock leg (buy-write / covered)." },
  { v: "TLAT", label: "TLAT", help: "Multi-leg tied-to-stock auction trade." },
  { v: "MASL", label: "MASL", help: "Multi-leg auction against single legs." },
  { v: "TFSL", label: "TFSL", help: "Tied-to-stock floor trade vs single legs." },
  { v: "CBMO", label: "CBMO", help: "Cabinet trade — nearly worthless contract closed for a token price." },
  { v: "ISOI", label: "ISOI", help: "Intermarket sweep order (ISO) fill." },
  { v: "EXHT", label: "EXHT", help: "Extended-hours trade (outside regular session)." },
  { v: "CANC", label: "CANC", help: "Canceled print — a previously reported trade was voided." },
  { v: "OSEQ", label: "OSEQ", help: "Out-of-sequence report — arrived late, not in time order." },
];

// Friendly tag chips → the raw feed tag each one filters on.
const TAG_CHIPS: { v: string; label: string; help: string }[] = [
  { v: "bullish", label: "Bullish", help: "Trades the feed marks as bullish (e.g. calls bought at the ask, puts sold)." },
  { v: "bearish", label: "Bearish", help: "Trades the feed marks as bearish (e.g. puts bought at the ask, calls sold)." },
  { v: "ask_side", label: "Buyer aggressive", help: "The trade hit the ask — the buyer paid up to get filled." },
  { v: "bid_side", label: "Seller aggressive", help: "The trade hit the bid — the seller took whatever the bid offered." },
  { v: "mid_side", label: "Mid-market fill", help: "The trade filled between bid and ask — usually negotiated." },
  { v: "etf", label: "ETF underlying", help: "The underlying is an ETF (SPY, QQQ, IWM…)." },
  { v: "index", label: "Index underlying", help: "The underlying is an index product (SPX, XSP, VIX…)." },
  { v: "earnings_this_week", label: "Earnings this week", help: "The company reports earnings within this week." },
  { v: "earnings_next_week", label: "Earnings next week", help: "The company reports earnings next week." },
  { v: "china", label: "China exposure", help: "Company flagged with significant China exposure." },
  { v: "volatility", label: "Volatility product", help: "Volatility-linked underlying (VIX family, vol ETPs)." },
  { v: "dividend", label: "Dividend play", help: "Flagged as a dividend-related trade (e.g. dividend capture)." },
];

// ── tiny CSV helpers over the draft params ────────────────────────────────────
const csvList = (v?: string) =>
  (v || "").split(",").map((s) => s.trim()).filter(Boolean);
const csvToggle = (v: string | undefined, item: string): string | null => {
  const set = new Set(csvList(v));
  if (set.has(item)) set.delete(item);
  else set.add(item);
  return set.size ? Array.from(set).join(",") : null;
};

export function FlowFilterSidebar({
  open,
  mobile = false,
  draft,
  dirty,
  activeCount,
  C,
  focusKey,
  onFocusSettled,
  onClose,
  onDraft,
  onApply,
}: Props) {
  const shellRef = useRef<HTMLDivElement | null>(null);

  // Height of the sticky site header above the panel (0 if none). Measured
  // live so the sidebar always fits exactly under it regardless of header
  // variant (logged-in vs logged-out render at different heights).
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector<HTMLElement>("header");
      setHeaderH(el ? Math.ceil(el.getBoundingClientRect().height) : 0);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    if (!open || !focusKey) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let tries = 0;
    const scrollToTarget = () => {
      tries += 1;
      const target = document.querySelector<HTMLElement>(`[data-flow-filter-keys~="${focusKey}"]`);
      if (target) {
        const shellEl = shellRef.current;
        if (shellEl) {
          const top = target.offsetTop - shellEl.clientHeight / 2 + target.offsetHeight / 2;
          shellEl.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
        } else {
          target.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        target.classList.remove("flow-filter-focus-shake");
        void target.offsetWidth;
        target.classList.add("flow-filter-focus-shake");
        window.setTimeout(() => target.classList.remove("flow-filter-focus-shake"), 650);
        const focusable = target.querySelector<HTMLElement>("input, button, [role='button']");
        focusable?.focus?.({ preventScroll: true });
        onFocusSettled?.();
        return;
      }
      if (tries < 8) timer = setTimeout(scrollToTarget, 80);
    };
    timer = setTimeout(scrollToTarget, 40);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [open, focusKey, onFocusSettled]);

  if (!open) return null;

  // Desktop: docked 330px panel beside the table. Phone: full-screen drawer
  // floating above the page (the docked panel would eat the whole viewport).
  // The page renders a sticky site header above this panel; subtract its
  // height or the bottom Apply bar hangs below the fold and never shows.
  const shell: React.CSSProperties = mobile
    ? {
        position: "fixed",
        inset: 0,
        width: "100%",
        height: "100dvh",
        zIndex: 200,
        background: C.panel,
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }
    : {
        width: 330,
        flexShrink: 0,
        background: C.panel,
        borderRight: `1px solid ${C.line}`,
        height: `calc(100vh - ${headerH}px)`,
        overflowY: "auto",
        position: "sticky",
        top: headerH,
        display: "flex",
        flexDirection: "column",
      };

  return (
    <div ref={shellRef} style={shell}>
      <style>{`
        @keyframes flowFilterFocusShake {
          0% { transform: translateX(0); background: transparent; }
          18% { transform: translateX(-2px); background: rgba(16,185,129,0.045); }
          36% { transform: translateX(2px); }
          54% { transform: translateX(-1px); }
          72% { transform: translateX(1px); background: rgba(16,185,129,0.025); }
          100% { transform: translateX(0); background: transparent; }
        }
        .flow-filter-focus-shake {
          animation: flowFilterFocusShake 520ms ease-out both;
          border-radius: 6px;
        }
      `}</style>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 18px",
          borderBottom: `1px solid ${C.line}`,
          position: "sticky",
          top: 0,
          background: C.panel,
          zIndex: 2,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              fontWeight: 600,
              color: C.text,
              fontSize: 13,
              letterSpacing: 2,
              textTransform: "uppercase",
            }}
          >
            Filters
          </span>
          {activeCount > 0 && (
            <span
              style={{
                border: `1px solid ${C.line}`,
                color: C.text,
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 700,
                padding: "1px 7px",
              }}
            >
              {activeCount}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button
            onClick={onClose}
            aria-label="Close filters"
            style={{
              background: "none",
              border: mobile ? `1px solid rgba(255,255,255,0.28)` : "none",
              borderRadius: 4,
              color: mobile ? C.text : C.textDim,
              cursor: "pointer",
              fontSize: 15,
              // Comfortable touch target on phones.
              padding: mobile ? "5px 11px" : 0,
            }}
          >
            ✕
          </button>
        </div>
      </div>

      <div style={{ padding: "0 18px 90px", flex: 1 }}>
        {/* ── CONTRACT ── */}
        <Section C={C} title="Contract" defaultOpen focusKey={focusKey} filterKeys={["ticker", "type", "min_strike", "max_strike", "min_dte", "max_dte", "expiry_dates", "opex_only", "hide_expired"]}>
          <Field C={C} label="Tickers" filterKeys={["ticker"]} help="One or more underlying symbols, comma separated. Example: NVDA, TSLA. Leave empty for the full market tape.">
            <TextInput
              C={C}
              value={draft.ticker || ""}
              placeholder="NVDA, TSLA…"
              transform={(v) => v.toUpperCase()}
              onChange={(v) => onDraft("ticker", v || null)}
              onEnter={onApply}
            />
          </Field>
          <Field C={C} label="Calls / Puts" filterKeys={["type"]} help="Show everything, only calls, or only puts. Hiding calls is the same as showing only puts.">
            <SegGroup
              C={C}
              value={draft.type || "all"}
              options={[
                { v: "all", label: "All" },
                { v: "call", label: "Calls", color: C.green },
                { v: "put", label: "Puts", color: C.red },
              ]}
              onChange={(v) => onDraft("type", v === "all" ? null : v)}
            />
          </Field>
          <Field C={C} label="Strike price ($)" filterKeys={["min_strike", "max_strike"]} help="Only contracts whose strike falls in this range.">
            <RangeRow C={C} minKey="min_strike" maxKey="max_strike" draft={draft} onDraft={onDraft} onEnter={onApply} />
          </Field>
          <Field C={C} label="Days to expiry" filterKeys={["min_dte", "max_dte"]} help="0 = expires today (0DTE). 30 = expires in about a month.">
            <RangeRow C={C} minKey="min_dte" maxKey="max_dte" draft={draft} onDraft={onDraft} onEnter={onApply} />
          </Field>
          <Field C={C} label="Specific expiry dates" filterKeys={["expiry_dates"]} help="Only prints on contracts expiring on the exact dates you pick. Monthly OpEx Fridays are marked with a dot — those carry the most open interest.">
            <ExpiryCalendar
              C={C}
              value={draft.expiry_dates}
              onChange={(v) => onDraft("expiry_dates", v)}
            />
          </Field>
          <ToggleRow C={C} draft={draft} onDraft={onDraft} items={[
            { k: "opex_only", label: "Monthly OpEx only", help: "Only contracts expiring on a monthly OpEx date (3rd Friday). These carry the most open interest." },
            { k: "hide_expired", label: "Hide expired", help: "Drop prints on contracts that have already expired." },
          ]} />
        </Section>

        {/* ── TRADE ── */}
        <Section C={C} title="Trade" defaultOpen focusKey={focusKey} filterKeys={["min_premium", "max_premium", "min_size", "max_size", "min_price", "max_price", "min_spread", "max_spread", "flag_types", "is_multi_leg", "is_single_leg", "opening", "intraday_only", "min_time", "max_time", "trade_codes", "aggregated", "show_canceled"]}>
          <Field C={C} label="Premium ($)" filterKeys={["min_premium", "max_premium"]} help="Total dollars paid for the trade (fill price × size × 100). min 100000 = only trades worth $100K+.">
            <RangeRow C={C} minKey="min_premium" maxKey="max_premium" draft={draft} onDraft={onDraft} onEnter={onApply} />
          </Field>
          <Field C={C} label="Size (contracts)" filterKeys={["min_size", "max_size"]} help="Number of contracts in the print.">
            <RangeRow C={C} minKey="min_size" maxKey="max_size" draft={draft} onDraft={onDraft} onEnter={onApply} />
          </Field>
          <Field C={C} label="Fill price ($)" filterKeys={["min_price", "max_price"]} help="Price paid per contract. 0.50 means fifty cents per share, $50 per contract.">
            <RangeRow C={C} minKey="min_price" maxKey="max_price" draft={draft} onDraft={onDraft} onEnter={onApply} step="0.01" />
          </Field>
          <Field C={C} label="Bid–ask spread ($)" filterKeys={["min_spread", "max_spread"]} help="How wide the market was when the trade printed. Tight spreads (small numbers) mean a liquid contract.">
            <RangeRow C={C} minKey="min_spread" maxKey="max_spread" draft={draft} onDraft={onDraft} onEnter={onApply} step="0.01" />
          </Field>
          <Field C={C} label="Execution type" filterKeys={["flag_types"]} help="Show only the checked execution styles. Sweep = aggressive order split across exchanges. Floor = negotiated on a trading floor. Cross = matched buyer/seller. Normal = everything else.">
            <ShowHideGrid
              C={C}
              value={draft.flag_types}
              options={[
                { v: "sweep", label: "Sweeps", help: "Aggressive order split across several exchanges at once to get filled fast. Often urgent, informed flow." },
                { v: "cross", label: "Crosses", help: "Buyer and seller were matched directly by a broker, then printed on the tape." },
                { v: "floor", label: "Floor", help: "Negotiated on a physical trading floor. Usually large institutional orders." },
                { v: "normal", label: "Normal", help: "A regular electronic fill with no special execution flag." },
              ]}
              onChange={(v) => onDraft("flag_types", v)}
            />
          </Field>
          <ToggleRow C={C} draft={draft} onDraft={onDraft} items={[
            { k: "is_multi_leg", label: "Multi-leg only", help: "Only prints that are part of a spread (multiple legs traded together)." },
            { k: "is_single_leg", label: "Single-leg only", help: "Only standalone prints — no spreads." },
            { k: "opening", label: "Opening trades", help: "Trades that open new positions (size larger than existing open interest suggests closing is impossible)." },
            { k: "intraday_only", label: "Today only", help: "Only prints from the current session — no overnight or extended-hours trades." },
          ]} />
          <Field C={C} label="Time window (ET)" filterKeys={["min_time", "max_time"]} help="Only prints executed between these times today, Eastern time. Pick a start and end from the dropdowns — e.g. 09:30 to 10:30 for the first hour.">
            <div style={{ display: "flex", gap: 8 }}>
              <TimeSelect C={C} value={draft.min_time || ""} placeholder="From" onChange={(v) => onDraft("min_time", v)} />
              <TimeSelect C={C} value={draft.max_time || ""} placeholder="To" onChange={(v) => onDraft("max_time", v)} />
            </div>
          </Field>
          <Field C={C} label="Exchange trade codes" filterKeys={["trade_codes"]} help="Raw OPRA condition codes — how the exchange labeled the execution. Hover each code for its meaning.">
            <MultiChips
              C={C}
              options={TRADE_CODES.map((t) => ({ v: t.v, label: t.label, help: t.help }))}
              value={draft.trade_codes}
              onChange={(v) => onDraft("trade_codes", v)}
            />
          </Field>
          <ToggleRow C={C} draft={draft} onDraft={onDraft} items={[
            { k: "aggregated", label: "Aggregate identical prints", help: "Collapse repeated identical prints (same contract, price, side) into one row with the combined size." },
            { k: "show_canceled", label: "Show canceled", help: "Include prints that were later canceled or modified. Hidden by default." },
          ]} />
        </Section>

        {/* ── SENTIMENT & AGGRESSOR ── */}
        <Section C={C} title="Sentiment & Aggressor" focusKey={focusKey} filterKeys={["sentiment", "sides", "min_ask_perc", "max_ask_perc", "min_bid_perc", "max_bid_perc", "min_skew", "max_skew"]}>
          <Field C={C} label="Sentiment" filterKeys={["sentiment"]} help="Bullish = betting the stock goes up. Bearish = betting it goes down. Derived from what was traded and on which side.">
            <SegGroup
              C={C}
              value={draft.sentiment || "any"}
              options={[
                { v: "any", label: "Any" },
                { v: "bullish", label: "Bull", color: C.green },
                { v: "bearish", label: "Bear", color: C.red },
                { v: "neutral", label: "Neutral", color: C.gold },
              ]}
              onChange={(v) => onDraft("sentiment", v === "any" ? null : v)}
            />
          </Field>
          <Field C={C} label="Fill side" filterKeys={["sides"]} help="Every option has two prices: the bid (what buyers offer) and the ask (what sellers want). Where a trade fills between them tells you who was in a hurry. Check the sides you want to see.">
            <ShowHideGrid
              C={C}
              value={draft.sides}
              options={[
                { v: "ask", label: "Ask (buyers)", color: C.green, help: "Filled at the ask: the buyer paid the seller's full price. Urgent buying." },
                { v: "bid", label: "Bid (sellers)", color: C.red, help: "Filled at the bid: the seller accepted the buyer's low price. Urgent selling." },
                { v: "mid", label: "Mid", color: C.gold, help: "Filled between bid and ask: neither side rushed — usually a negotiated fill." },
                { v: "none", label: "No side", help: "Prints where the fill side couldn't be determined (e.g. quotes were crossed at the time)." },
              ]}
              onChange={(v) => onDraft("sides", v)}
            />
          </Field>
          <Field C={C} label="Ask-side volume share" filterKeys={["min_ask_perc", "max_ask_perc"]} help="What fraction of today's volume in this contract traded at the ask. 0.7+ means mostly aggressive buying. Range 0–1.">
            <RangeRow C={C} minKey="min_ask_perc" maxKey="max_ask_perc" draft={draft} onDraft={onDraft} onEnter={onApply} step="0.05" />
          </Field>
          <Field C={C} label="Bid-side volume share" filterKeys={["min_bid_perc", "max_bid_perc"]} help="What fraction of today's volume traded at the bid. 0.7+ means mostly aggressive selling. Range 0–1.">
            <RangeRow C={C} minKey="min_bid_perc" maxKey="max_bid_perc" draft={draft} onDraft={onDraft} onEnter={onApply} step="0.05" />
          </Field>
          <Field C={C} label="Decided volume share" filterKeys={["min_skew", "max_skew"]} help="Fraction of volume that traded at the bid OR ask (not mid). High = conviction flow, low = negotiated fills. Range 0–1.">
            <RangeRow C={C} minKey="min_skew" maxKey="max_skew" draft={draft} onDraft={onDraft} onEnter={onApply} step="0.05" />
          </Field>
        </Section>

        {/* ── GREEKS & VOLATILITY ── */}
        <Section C={C} title="Greeks & Volatility" focusKey={focusKey} filterKeys={["min_delta", "max_delta", "min_gamma", "max_gamma", "min_theta", "max_theta", "min_iv", "max_iv"]}>
          <Field C={C} label="Delta" filterKeys={["min_delta", "max_delta"]} help="How much the option moves per $1 move in the stock. 0.5 ≈ at-the-money. Near 0 or ±1 = far from the money. Puts are negative.">
            <RangeRow C={C} minKey="min_delta" maxKey="max_delta" draft={draft} onDraft={onDraft} onEnter={onApply} step="0.05" />
          </Field>
          <Field C={C} label="Gamma" filterKeys={["min_gamma", "max_gamma"]} help="How fast delta changes as the stock moves. High gamma = explosive near-the-money short-dated contracts.">
            <RangeRow C={C} minKey="min_gamma" maxKey="max_gamma" draft={draft} onDraft={onDraft} onEnter={onApply} step="0.001" />
          </Field>
          <Field C={C} label="Theta" filterKeys={["min_theta", "max_theta"]} help="Daily time decay in dollars. -0.05 means the contract bleeds 5 cents a day. Always negative for long options.">
            <RangeRow C={C} minKey="min_theta" maxKey="max_theta" draft={draft} onDraft={onDraft} onEnter={onApply} step="0.001" />
          </Field>
          <Field C={C} label="Implied volatility" filterKeys={["min_iv", "max_iv"]} help="The market's priced-in expectation of movement. 0.5 = 50% annualized. Higher = more expensive options.">
            <RangeRow C={C} minKey="min_iv" maxKey="max_iv" draft={draft} onDraft={onDraft} onEnter={onApply} step="0.01" />
          </Field>
        </Section>

        {/* ── VOLUME & OPEN INTEREST ── */}
        <Section C={C} title="Volume & Open Interest" focusKey={focusKey} filterKeys={["min_volume", "max_volume", "min_open_interest", "max_open_interest", "min_vol_oi_ratio", "max_vol_oi_ratio", "unusual", "vol_greater_oi", "size_greater_oi"]}>
          <Field C={C} label="Contract volume today" filterKeys={["min_volume", "max_volume"]} help="Total contracts traded today in this specific contract.">
            <RangeRow C={C} minKey="min_volume" maxKey="max_volume" draft={draft} onDraft={onDraft} onEnter={onApply} />
          </Field>
          <Field C={C} label="Open interest" filterKeys={["min_open_interest", "max_open_interest"]} help="Contracts currently held open from previous days. Low OI + big volume = something new happening.">
            <RangeRow C={C} minKey="min_open_interest" maxKey="max_open_interest" draft={draft} onDraft={onDraft} onEnter={onApply} />
          </Field>
          <Field C={C} label="Volume ÷ open interest" filterKeys={["min_vol_oi_ratio", "max_vol_oi_ratio"]} help="Today's volume relative to existing positions. Above 1 = unusual: more traded today than was held open.">
            <RangeRow C={C} minKey="min_vol_oi_ratio" maxKey="max_vol_oi_ratio" draft={draft} onDraft={onDraft} onEnter={onApply} step="0.1" />
          </Field>
          <ToggleRow C={C} draft={draft} onDraft={onDraft} items={[
            { k: "unusual", label: "Unusual", help: "Only prints that triggered an Unusual Whales flow-alert rule — pre-screened noteworthy trades." },
            { k: "vol_greater_oi", label: "Volume > OI", help: "Today's volume exceeds open interest — the classic 'unusual activity' signal." },
            { k: "size_greater_oi", label: "Size > OI", help: "This single print is bigger than the entire existing open interest. Very rare, very deliberate." },
          ]} />
        </Section>

        {/* ── MONEYNESS ── */}
        <Section C={C} title="Moneyness" focusKey={focusKey} filterKeys={["min_diff", "max_diff", "min_underlying_price", "max_underlying_price", "is_otm", "exclude_deep_itm"]}>
          <Field C={C} label="% out of the money" filterKeys={["min_diff", "max_diff"]} help="How far the strike is from the stock price. 0.05 = 5% OTM. Negative = in the money.">
            <RangeRow C={C} minKey="min_diff" maxKey="max_diff" draft={draft} onDraft={onDraft} onEnter={onApply} step="0.01" />
          </Field>
          <Field C={C} label="Stock price ($)" filterKeys={["min_underlying_price", "max_underlying_price"]} help="Only underlyings trading in this price range.">
            <RangeRow C={C} minKey="min_underlying_price" maxKey="max_underlying_price" draft={draft} onDraft={onDraft} onEnter={onApply} />
          </Field>
          <ToggleRow C={C} draft={draft} onDraft={onDraft} items={[
            { k: "is_otm", label: "OTM only", help: "Only out-of-the-money contracts — pure directional bets." },
            { k: "exclude_deep_itm", label: "No deep ITM", help: "Drop contracts more than ~15% in the money (often stock-replacement, not directional flow)." },
          ]} />
        </Section>

        {/* ── COMPANY ── */}
        <Section C={C} title="Company" focusKey={focusKey} filterKeys={["issue_types", "sectors", "industries", "exchanges", "min_marketcap", "max_marketcap"]}>
          <Field C={C} label="Underlying type" filterKeys={["issue_types"]} help="Check the kinds of underlyings you want. Uncheck Indices to remove the SPX/XSP firehose.">
            <ShowHideGrid
              C={C}
              value={draft.issue_types}
              options={ISSUE_TYPES.map((t) => ({ v: t.v, label: t.label }))}
              onChange={(v) => onDraft("issue_types", v)}
            />
          </Field>
          <Field C={C} label="Sectors" filterKeys={["sectors"]} help="Only companies in the selected sectors. Index products have no sector.">
            <MultiChips C={C} options={SECTORS.map((s) => ({ v: s, label: s }))} value={draft.sectors} onChange={(v) => onDraft("sectors", v)} />
          </Field>
          <Field C={C} label="Industries" filterKeys={["industries"]} help="Only companies in the selected industries. Type to search the list.">
            <SearchableMultiSelect
              C={C}
              options={INDUSTRIES.map((s) => ({ v: s, label: s }))}
              value={draft.industries}
              placeholder="Search industries…"
              onChange={(v) => onDraft("industries", v)}
            />
          </Field>
          <Field C={C} label="Exchanges" filterKeys={["exchanges"]} help="Only prints executed on the selected options exchanges.">
            <MultiChips C={C} options={EXCHANGES} value={draft.exchanges} onChange={(v) => onDraft("exchanges", v)} />
          </Field>
          <Field C={C} label="Market cap ($)" filterKeys={["min_marketcap", "max_marketcap"]} help="Company size in dollars. 1000000000 = $1B. Indices and some ETFs have none.">
            <RangeRow C={C} minKey="min_marketcap" maxKey="max_marketcap" draft={draft} onDraft={onDraft} onEnter={onApply} />
          </Field>
        </Section>

        {/* ── EARNINGS ── */}
        <Section C={C} title="Earnings" focusKey={focusKey} filterKeys={["min_earnings_dte", "max_earnings_dte", "min_earnings_gap", "max_earnings_gap", "avoid_earnings", "earnings_week"]}>
          <Field C={C} label="Days until earnings" filterKeys={["min_earnings_dte", "max_earnings_dte"]} help="How many days until the company's next earnings report. 0–7 = reporting within a week.">
            <RangeRow C={C} minKey="min_earnings_dte" maxKey="max_earnings_dte" draft={draft} onDraft={onDraft} onEnter={onApply} />
          </Field>
          <Field C={C} label="Expiry vs earnings gap (days)" filterKeys={["min_earnings_gap", "max_earnings_gap"]} help="Days between the contract's expiry and the earnings date. Negative = expires before earnings (no earnings risk). Positive = holds through earnings.">
            <RangeRow C={C} minKey="min_earnings_gap" maxKey="max_earnings_gap" draft={draft} onDraft={onDraft} onEnter={onApply} />
          </Field>
          <ToggleRow C={C} draft={draft} onDraft={onDraft} items={[
            { k: "avoid_earnings", label: "Avoid earnings", help: "Only contracts that expire BEFORE the next earnings report — no earnings surprise risk." },
            { k: "earnings_week", label: "Earnings week", help: "Only companies reporting earnings within the next 7 days." },
          ]} />
        </Section>

        {/* ── TAGS ── */}
        <Section C={C} title="Tags" focusKey={focusKey} filterKeys={["tags", "excluded_tags"]}>
          <Field C={C} label="Must have one of" filterKeys={["tags"]} help="Keep only trades carrying at least one of the selected tags.">
            <MultiChips
              C={C}
              options={TAG_CHIPS.map((t) => ({ v: t.v, label: t.label, help: t.help }))}
              value={draft.tags}
              onChange={(v) => onDraft("tags", v)}
            />
          </Field>
          <Field C={C} label="Hide trades tagged" filterKeys={["excluded_tags"]} help="Drop any trade carrying one of the selected tags.">
            <MultiChips
              C={C}
              options={TAG_CHIPS.map((t) => ({ v: t.v, label: t.label, help: t.help }))}
              value={draft.excluded_tags}
              onChange={(v) => onDraft("excluded_tags", v)}
            />
          </Field>
        </Section>
      </div>

      {/* Sticky Apply bar */}
      <div
        style={{
          position: "sticky",
          bottom: 0,
          background: C.panel,
          borderTop: `1px solid ${C.line}`,
          padding: "12px 18px",
          zIndex: 2,
        }}
      >
        <button
          onClick={onApply}
          disabled={!dirty}
          style={{
            width: "100%",
            padding: "11px 0",
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: "uppercase",
            cursor: dirty ? "pointer" : "default",
            border: `1px solid ${dirty ? C.accent : C.line}`,
            background: dirty ? C.accent : "transparent",
            color: dirty ? "#000" : C.textDim,
            boxShadow: dirty ? `0 0 24px ${C.accentDim}` : "none",
            transition: "all 160ms ease",
          }}
        >
          {dirty ? "Apply filters" : "Filters applied"}
        </button>
      </div>
    </div>
  );
}

// ── Building blocks ───────────────────────────────────────────────────────────

function Section({
  C,
  title,
  defaultOpen = false,
  filterKeys,
  focusKey,
  children,
}: {
  C: Palette;
  title: string;
  defaultOpen?: boolean;
  filterKeys?: string[];
  focusKey?: string | null;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (focusKey && filterKeys?.includes(focusKey)) setOpen(true);
  }, [focusKey, filterKeys]);
  return (
    <div style={{ borderBottom: `1px solid ${C.lineSoft}` }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "none",
          border: "none",
          cursor: "pointer",
          padding: "14px 0",
          color: C.text,
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: 2, textTransform: "uppercase" }}>
          {title}
        </span>
        <span style={{ color: C.textDim, fontSize: 10 }}>{open ? "—" : "+"}</span>
      </button>
      {open && <div style={{ paddingBottom: 16 }}>{children}</div>}
    </div>
  );
}

/**
 * Portal tooltip. Renders into document.body so it can NEVER be clipped by the
 * sidebar's overflow, never causes a scrollbar, and always floats above
 * everything. Position is computed from the trigger's rect and clamped to the
 * viewport (prefers right of the trigger, flips left when there's no room).
 */
function TipBubble({ C, text, anchor }: { C: Palette; text: string; anchor: DOMRect }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const pad = 8;
    // Prefer right of the anchor; flip to the left when it would overflow.
    let left = anchor.right + 10;
    if (left + w + pad > window.innerWidth) left = anchor.left - w - 10;
    if (left < pad) left = pad;
    // Vertically center on the anchor, clamped to the viewport.
    let top = anchor.top + anchor.height / 2 - h / 2;
    top = Math.max(pad, Math.min(top, window.innerHeight - h - pad));
    setPos({ left, top });
  }, [anchor]);

  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      ref={ref}
      style={{
        position: "fixed",
        left: pos ? pos.left : -9999,
        top: pos ? pos.top : -9999,
        zIndex: 1000,
        width: 236,
        background: "#0d0d0d",
        border: `1px solid rgba(255,255,255,0.18)`,
        borderRadius: 6,
        padding: "9px 11px",
        fontSize: 11.5,
        lineHeight: 1.55,
        color: C.text,
        textTransform: "none",
        letterSpacing: 0.2,
        fontWeight: 400,
        boxShadow: "0 12px 40px rgba(0,0,0,0.95)",
        pointerEvents: "none",
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {text}
    </div>,
    document.body
  );
}

/** Wrap ANY element; hovering it shows a portal tooltip next to it.
 *  Exported — FlowClient reuses it for table-header help tooltips. */
export function HoverTip({
  C,
  text,
  children,
  style,
}: {
  C: Palette;
  text?: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const ref = useRef<HTMLSpanElement | null>(null);
  if (!text) return <>{children}</>;
  return (
    <span
      ref={ref}
      style={{ display: "inline-flex", ...style }}
      onMouseEnter={() => ref.current && setAnchor(ref.current.getBoundingClientRect())}
      onMouseLeave={() => setAnchor(null)}
    >
      {children}
      {anchor && <TipBubble C={C} text={text} anchor={anchor} />}
    </span>
  );
}

/** The small "?" trigger, now backed by the portal tooltip. */
function Help({ C, text }: { C: Palette; text: string }) {
  return (
    <HoverTip C={C} text={text} style={{ marginLeft: 6 }}>
      <span
        style={{
          width: 13,
          height: 13,
          borderRadius: 999,
          border: `1px solid rgba(255,255,255,0.3)`,
          color: "rgba(255,255,255,0.7)",
          fontSize: 9,
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "help",
          lineHeight: 1,
        }}
      >
        ?
      </span>
    </HoverTip>
  );
}

function Field({
  C,
  label,
  filterKeys,
  help,
  children,
}: {
  C: Palette;
  label: string;
  filterKeys?: string[];
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div data-flow-filter-keys={filterKeys?.join(" ")} style={{ marginBottom: 14 }}>
      <label
        style={{
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: 0.8,
          textTransform: "uppercase",
          color: C.text,
          marginBottom: 6,
          display: "flex",
          alignItems: "center",
        }}
      >
        {label}
        {help && <Help C={C} text={help} />}
      </label>
      {children}
    </div>
  );
}

function TextInput({
  C,
  value,
  placeholder,
  onChange,
  onEnter,
  transform,
}: {
  C: Palette;
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
  onEnter?: () => void;
  transform?: (v: string) => string;
}) {
  return (
    <input
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(transform ? transform(e.target.value) : e.target.value)}
      onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
      style={{
        width: "100%",
        background: C.bg,
        border: `1px solid rgba(255,255,255,0.28)`,
        borderRadius: 6,
        color: C.text,
        padding: "8px 10px",
        fontSize: 12,
        outline: "none",
      }}
    />
  );
}

// Session clock choices for the time-window selector: pre-market 04:00 →
// after-hours 20:00, every 30 minutes (09:30 open and 16:00 close included).
const TIME_CHOICES: string[] = (() => {
  const out: string[] = [];
  for (let h = 4; h <= 20; h++) {
    out.push(`${String(h).padStart(2, "0")}:00`);
    if (h < 20) out.push(`${String(h).padStart(2, "0")}:30`);
  }
  return out;
})();

/** Styled dropdown time picker (no typing) for the ET time window. */
function TimeSelect({
  C,
  value,
  placeholder,
  onChange,
}: {
  C: Palette;
  value: string;
  placeholder: string;
  onChange: (v: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative", width: "50%" }} tabIndex={-1} onBlur={(e) => {
      // Close when focus leaves this control entirely.
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
    }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: C.bg,
          border: `1px solid ${open ? C.accent : "rgba(255,255,255,0.28)"}`,
          borderRadius: 6,
          color: value ? C.text : C.textDim,
          padding: "8px 10px",
          fontSize: 12,
          cursor: "pointer",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        <span>{value || placeholder}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={open ? C.accent : "rgba(255,255,255,0.6)"} strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 3" />
        </svg>
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            background: "#0a0a0a",
            border: `1px solid rgba(255,255,255,0.28)`,
            borderRadius: 6,
            maxHeight: 190,
            overflowY: "auto",
            boxShadow: "0 12px 40px rgba(0,0,0,0.9)",
          }}
        >
          {value && (
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                onChange(null);
                setOpen(false);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "7px 10px",
                color: C.red,
                fontSize: 11.5,
                fontWeight: 600,
              }}
            >
              Clear
            </button>
          )}
          {TIME_CHOICES.map((t) => {
            const active = t === value;
            return (
              <button
                key={t}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onChange(t);
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  textAlign: "left",
                  background: active ? "rgba(255,255,255,0.06)" : "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "7px 10px",
                  color: active ? C.accent : C.text,
                  fontSize: 11.5,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {t}
                {active && <span style={{ fontSize: 10 }}>✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SegGroup({
  C,
  value,
  options,
  onChange,
}: {
  C: Palette;
  value: string;
  options: { v: string; label: string; color?: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {options.map((o) => {
        const active = value === o.v;
        const col = o.color || C.text;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            style={{
              flex: 1,
              padding: "7px 4px",
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.5,
              cursor: "pointer",
              border: `1px solid ${active ? col : "rgba(255,255,255,0.28)"}`,
              background: active ? "rgba(255,255,255,0.06)" : "transparent",
              color: active ? col : C.text,
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * UW-style show/hide checklist over a categorical field.
 * Empty param = everything shown. A CSV value = allow-list.
 * Unchecking a box removes that category (i.e. "hide").
 */
function ShowHideGrid({
  C,
  value,
  options,
  onChange,
}: {
  C: Palette;
  value: string | undefined;
  options: { v: string; label: string; color?: string; help?: string }[];
  onChange: (v: string | null) => void;
}) {
  const selected = csvList(value);
  const all = selected.length === 0; // no param = all shown
  const isOn = (v: string) => all || selected.includes(v);
  const toggle = (v: string) => {
    let next: string[];
    if (all) {
      // Currently everything shown → unchecking one keeps the rest.
      next = options.map((o) => o.v).filter((x) => x !== v);
    } else if (selected.includes(v)) {
      next = selected.filter((x) => x !== v);
      if (next.length === 0) next = options.map((o) => o.v).filter((x) => x !== v); // never allow empty
    } else {
      next = [...selected, v];
    }
    // All selected again → drop the param entirely.
    onChange(next.length >= options.length ? null : next.join(","));
  };
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {options.map((o) => {
        const on = isOn(o.v);
        const col = o.color || C.accent;
        return (
          <HoverTip key={o.v} C={C} text={o.help}>
            <button
              onClick={() => toggle(o.v)}
              style={{
                // ONE consistent look for every chip: white text always; the
                // ONLY state signal is the checkbox fill + border color.
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                border: `1px solid ${on ? col : "rgba(255,255,255,0.28)"}`,
                background: on ? "rgba(255,255,255,0.04)" : "transparent",
                color: C.text,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  border: `1px solid ${on ? col : "rgba(255,255,255,0.4)"}`,
                  background: on ? col : "transparent",
                  flexShrink: 0,
                }}
              />
              {o.label}
            </button>
          </HoverTip>
        );
      })}
    </div>
  );
}

function ToggleRow({
  C,
  draft,
  onDraft,
  items,
}: {
  C: Palette;
  draft: FlowParams;
  onDraft: (k: string, v: string | null) => void;
  items: { k: string; label: string; help?: string }[];
}) {
  return (
    <div data-flow-filter-keys={items.map((i) => i.k).join(" ")} style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
      {items.map(({ k, label, help }) => {
        const on = draft[k] === "true";
        return (
          <HoverTip key={k} C={C} text={help}>
            <button
              onClick={() => onDraft(k, on ? null : "true")}
              style={{
                padding: "6px 10px",
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 600,
                cursor: "pointer",
                border: `1px solid ${on ? C.accent : "rgba(255,255,255,0.28)"}`,
                background: on ? C.accentDim : "transparent",
                color: on ? C.accent : C.text,
              }}
            >
              {label}
            </button>
          </HoverTip>
        );
      })}
    </div>
  );
}

function RangeRow({
  C,
  minKey,
  maxKey,
  draft,
  onDraft,
  onEnter,
  step,
}: {
  C: Palette;
  minKey: string;
  maxKey: string;
  draft: FlowParams;
  onDraft: (k: string, v: string | null) => void;
  onEnter?: () => void;
  step?: string;
}) {
  const inputStyle: React.CSSProperties = {
    width: "50%",
    background: C.bg,
    border: `1px solid rgba(255,255,255,0.28)`,
    borderRadius: 6,
    color: C.text,
    padding: "7px 10px",
    fontSize: 12,
    outline: "none",
  };
  return (
    <div style={{ display: "flex", gap: 8 }}>
      <input
        type="number"
        step={step}
        value={draft[minKey] || ""}
        placeholder="min"
        onChange={(e) => onDraft(minKey, e.target.value || null)}
        onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
        style={inputStyle}
      />
      <input
        type="number"
        step={step}
        value={draft[maxKey] || ""}
        placeholder="max"
        onChange={(e) => onDraft(maxKey, e.target.value || null)}
        onKeyDown={(e) => e.key === "Enter" && onEnter?.()}
        style={inputStyle}
      />
    </div>
  );
}

function MultiChips({
  C,
  options,
  value,
  onChange,
}: {
  C: Palette;
  options: { v: string; label: string; help?: string }[];
  value: string | undefined;
  onChange: (v: string | null) => void;
}) {
  const selected = csvList(value);
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {options.map((o) => {
        const on = selected.includes(o.v);
        return (
          <HoverTip key={o.v} C={C} text={o.help}>
            <button
              onClick={() => onChange(csvToggle(value, o.v))}
              style={{
                padding: "5px 9px",
                borderRadius: 6,
                fontSize: 11,
                fontWeight: 500,
                cursor: "pointer",
                border: `1px solid ${on ? C.accent : "rgba(255,255,255,0.28)"}`,
                background: on ? C.accentDim : "transparent",
                color: on ? C.accent : C.text,
              }}
            >
              {o.label}
            </button>
          </HoverTip>
        );
      })}
    </div>
  );
}

/**
 * Searchable multi-select for long option lists (industries). Selected values
 * show as removable chips; typing filters the dropdown. In-flow (no modal).
 */
function SearchableMultiSelect({
  C,
  options,
  value,
  placeholder,
  onChange,
}: {
  C: Palette;
  options: { v: string; label: string }[];
  value: string | undefined;
  placeholder: string;
  onChange: (v: string | null) => void;
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = csvList(value);
  const matches = q
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()) && !selected.includes(o.v))
    : options.filter((o) => !selected.includes(o.v));

  const pick = (v: string) => {
    onChange(csvToggle(value, v));
    setQ("");
  };

  return (
    <div>
      <input
        value={q}
        placeholder={placeholder}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={{
          width: "100%",
          background: C.bg,
          border: `1px solid ${open ? C.accent : "rgba(255,255,255,0.28)"}`,
          borderRadius: 6,
          color: C.text,
          padding: "8px 10px",
          fontSize: 12,
          outline: "none",
        }}
      />
      {selected.length > 0 && (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 7 }}>
          {selected.map((s) => (
            <button
              key={s}
              onClick={() => onChange(csvToggle(value, s))}
              title="Remove"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "3px 8px",
                borderRadius: 999,
                fontSize: 10.5,
                fontWeight: 600,
                cursor: "pointer",
                border: `1px solid ${C.accent}`,
                background: C.accentDim,
                color: C.accent,
              }}
            >
              {s}
              <span style={{ fontSize: 9 }}>✕</span>
            </button>
          ))}
        </div>
      )}
      {open && matches.length > 0 && (
        <div
          style={{
            marginTop: 6,
            border: `1px solid ${C.line}`,
            borderRadius: 6,
            background: "#0a0a0a",
            maxHeight: 190,
            overflowY: "auto",
          }}
        >
          {matches.slice(0, 40).map((o) => (
            <button
              key={o.v}
              onMouseDown={(e) => {
                e.preventDefault(); // fire before the input's blur
                pick(o.v);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "7px 10px",
                color: C.text,
                fontSize: 11.5,
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Inline expiry calendar (no modal): dropdown anchored in-flow under the
//    trigger, multi-select, selected dates shown as removable chips, monthly
//    OpEx Fridays (3rd Friday) marked with a dot. ──────────────────────────────
const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const iso = (y: number, m: number, d: number) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

function isOpexFriday(y: number, m: number, d: number): boolean {
  const date = new Date(y, m, d);
  if (date.getDay() !== 5) return false;
  return d >= 15 && d <= 21; // 3rd Friday always falls on the 15th–21st
}

function ExpiryCalendar({
  C,
  value,
  onChange,
}: {
  C: Palette;
  value: string | undefined;
  onChange: (v: string | null) => void;
}) {
  const selected = csvList(value);
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [ym, setYm] = useState<{ y: number; m: number }>({ y: today.getFullYear(), m: today.getMonth() });

  const toggleDate = (d: string) => {
    const set = new Set(selected);
    if (set.has(d)) set.delete(d);
    else set.add(d);
    const arr = Array.from(set).sort();
    onChange(arr.length ? arr.join(",") : null);
  };

  const first = new Date(ym.y, ym.m, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(ym.y, ym.m + 1, 0).getDate();
  const todayIso = iso(today.getFullYear(), today.getMonth(), today.getDate());

  const cells: (number | null)[] = [
    ...Array.from({ length: startPad }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  return (
    <div>
      {/* Trigger — the selected dates live INSIDE the input as removable
          chips, exactly like a multi-select. Clicking empty space toggles the
          calendar; clicking a chip's ✕ removes just that date. */}
      <div
        onClick={() => setOpen((o) => !o)}
        role="button"
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 5,
          background: C.bg,
          border: `1px solid ${open ? C.accent : "rgba(255,255,255,0.28)"}`,
          borderRadius: 6,
          color: selected.length ? C.text : C.textDim,
          padding: "6px 10px",
          minHeight: 34,
          fontSize: 12,
          cursor: "pointer",
        }}
      >
        {selected.length === 0 && <span>Pick dates…</span>}
        {selected.map((d) => (
          <span
            key={d}
            onClick={(e) => {
              e.stopPropagation();
              toggleDate(d);
            }}
            title="Remove"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "2px 8px",
              borderRadius: 999,
              fontSize: 10.5,
              fontWeight: 600,
              cursor: "pointer",
              border: `1px solid ${C.accent}`,
              background: C.accentDim,
              color: C.accent,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {d}
            <span style={{ fontSize: 9 }}>✕</span>
          </span>
        ))}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke={open ? C.accent : "rgba(255,255,255,0.6)"}
          strokeWidth="2"
          strokeLinecap="round"
          style={{ marginLeft: "auto", flexShrink: 0 }}
        >
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 11h18" />
        </svg>
      </div>

      {open && (
        <div
          style={{
            marginTop: 8,
            border: `1px solid ${C.line}`,
            borderRadius: 8,
            background: "#0a0a0a",
            padding: 10,
          }}
        >
          {/* Month nav */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <button
              onClick={() => setYm(({ y, m }) => (m === 0 ? { y: y - 1, m: 11 } : { y, m: m - 1 }))}
              style={{ background: "none", border: `1px solid ${C.line}`, borderRadius: 4, color: C.text, cursor: "pointer", padding: "2px 8px", fontSize: 12 }}
            >
              ‹
            </button>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: C.text, letterSpacing: 0.5 }}>
              {MONTHS[ym.m]} {ym.y}
            </span>
            <button
              onClick={() => setYm(({ y, m }) => (m === 11 ? { y: y + 1, m: 0 } : { y, m: m + 1 }))}
              style={{ background: "none", border: `1px solid ${C.line}`, borderRadius: 4, color: C.text, cursor: "pointer", padding: "2px 8px", fontSize: 12 }}
            >
              ›
            </button>
          </div>

          {/* Day-of-week header */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 2 }}>
            {DOW.map((d) => (
              <div key={d} style={{ textAlign: "center", fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.55)", letterSpacing: 0.5, padding: "3px 0" }}>
                {d}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
            {cells.map((d, i) => {
              if (d == null) return <div key={`pad-${i}`} />;
              const dIso = iso(ym.y, ym.m, d);
              const on = selected.includes(dIso);
              const past = dIso < todayIso;
              const opex = isOpexFriday(ym.y, ym.m, d);
              const isToday = dIso === todayIso;
              return (
                <button
                  key={dIso}
                  onClick={() => toggleDate(dIso)}
                  style={{
                    position: "relative",
                    padding: "6px 0",
                    borderRadius: 5,
                    fontSize: 11,
                    fontWeight: on ? 700 : 500,
                    cursor: "pointer",
                    border: `1px solid ${on ? C.accent : isToday ? "rgba(255,255,255,0.35)" : "transparent"}`,
                    background: on ? C.accent : "transparent",
                    color: on ? "#000" : past ? "rgba(255,255,255,0.3)" : C.text,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {d}
                  {opex && (
                    <span
                      style={{
                        position: "absolute",
                        bottom: 2,
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 3,
                        height: 3,
                        borderRadius: 999,
                        background: on ? "#000" : C.gold,
                      }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend + clear */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 9.5, color: "rgba(255,255,255,0.6)" }}>
              <span style={{ width: 4, height: 4, borderRadius: 999, background: C.gold }} />
              Monthly OpEx (3rd Friday)
            </span>
            {selected.length > 0 && (
              <button
                onClick={() => onChange(null)}
                style={{ background: "none", border: "none", color: C.red, cursor: "pointer", fontSize: 10, fontWeight: 600, letterSpacing: 0.5 }}
              >
                Clear all
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
